import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSidePropsContext } from 'next';
import { NextSeo } from 'next-seo';
import { match } from 'ts-pattern';
import { Creator } from '~/clientApi/creator.model';
import { SavedList } from '~/clientApi/savedLists.model';
import { MarketingBanner } from '~/components/MarketingBanner';
import { SHARED_ENTITIES } from '~/components/MarketingBanner/MarketingBanner';
import { getSharedSavedListData } from '~/hooks/api/useSavedLists';
import { SharedLayout } from '~/layouts/SharedLayout';
import { processOgImages } from '~/metaDataUtils';
import { SHARED_SAVED_LISTS } from '~/queries/keys';
import { useActiveView } from '~/screens/MyAgency/hooks/useActiveView';
import { SharedSavedListScreen } from '~/screens/SharedSavedList';
import { WhalarModeSharedListScreen } from '~/screens/WhalarModeSharedListScreen/WhalarModeSharedListScreen';

interface Props {
  savedList: SavedList;
  urlImagePreview?: string;
  sharedToken: string;
}

const SharedSavedList: React.FC<Props> = ({
  savedList,
  urlImagePreview,
  sharedToken,
}) => {
  const { activeView } = useActiveView();

  return (
    <SharedLayout variant={activeView === 'list' ? 'fluid' : 'restricted'}>
      <NextSeo
        title={`${savedList?.name} | Foam`}
        openGraph={{
          title: savedList?.name,
          description: savedList?.agencyName,
          type: 'website',
          images: urlImagePreview
            ? processOgImages([urlImagePreview], 'list')
            : [],
          siteName: 'FOAM',
          url: `${process.env.NEXT_PUBLIC_APP_DOMAIN}/shared/t/${sharedToken}`,
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />
      {match(savedList?.category)
        .with('sourcing-list', () => <WhalarModeSharedListScreen />)
        .otherwise(() => (
          <SharedSavedListScreen />
        ))}
      <MarketingBanner
        hide={!savedList?.agencyName}
        agencyName={savedList?.agencyName}
        entity={SHARED_ENTITIES.playlist}
        trackId="foam-marketing-banner"
      />
    </SharedLayout>
  );
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const queryClient = new QueryClient();
  const sharedToken = `share-link-${context.params?.id}`;

  // Prefetch the data
  await queryClient.prefetchQuery({
    queryKey: [SHARED_SAVED_LISTS, { token: sharedToken }],
    queryFn: () => getSharedSavedListData({ sharedToken }),
  });

  const savedList = queryClient.getQueryData([
    SHARED_SAVED_LISTS,
    { token: sharedToken },
  ]) as SavedList;

  let urlImagePreview = null;

  const BASE_URL_SUFFIX = 'saved-list';
  const baseUrl = `${process.env.NEXT_PUBLIC_OG_IMAGES_API}/${BASE_URL_SUFFIX}`;
  const listTalent = savedList?.firstFourCreators;
  const agencyLogo = savedList?.agencyLogo;
  const formatedPreviewAvatars = listTalent
    .map((talent: Creator, idx: number) =>
      talent.avatar
        ? `talent${idx + 1}=${talent.avatar}`
        : `talent${idx + 1}=https://assets.usefoam.com/empty_list.svg`,
    )
    .toString()
    .replace(/,/g, '&');

  const listNameParam = `?list=${encodeURIComponent(savedList?.name)}`;
  const avatarParam = `&${formatedPreviewAvatars}`;
  const logoParam = agencyLogo ? `&agencyLogo=${agencyLogo}` : '';

  urlImagePreview = `${baseUrl}${listNameParam}${logoParam}${avatarParam}`;

  return {
    props: {
      isSSR: true,
      dehydratedState: dehydrate(queryClient),
      savedList,
      urlImagePreview,
      sharedToken: context.params?.id,
    },
  };
}

export default SharedSavedList;
